import React, { useState } from 'react';
import { Scale, FileText, Phone, ExternalLink, AlertCircle, Search, ChevronRight, BookOpen, Users, Shield, Clipboard } from '../components/CustomIcons';

const LEGAL_CATEGORIES = [
  { id: 'expungement', label: 'Expungement & Record Sealing', icon: Shield, color: '#F97316' },
  { id: 'probation', label: 'Parole & Probation', icon: Clipboard, color: '#F97316' },
  { id: 'employment', label: 'Employment Rights', icon: Users, color: '#F97316' },
  { id: 'housing', label: 'Housing Rights', icon: FileText, color: '#F97316' },
  { id: 'family', label: 'Family & Custody', icon: Users, color: '#F97316' },
  { id: 'appeals', label: 'Appeals & Post-Conviction', icon: BookOpen, color: '#F97316' },
];

const LEGAL_AID = [
  {
    id: 'la1', name: 'Texas Legal Services Center', phone: '1-888-988-9996', website: 'txlegal.org',
    serves: 'TX', desc: 'Free civil legal assistance for low-income individuals including housing, benefits, family law.',
    areas: ['Housing', 'Family Law', 'Benefits']
  },
  {
    id: 'la2', name: 'Lone Star Legal Aid', phone: '1-800-733-8394', website: 'lonestarlegal.org',
    serves: 'TX', desc: 'Legal representation for low-income Texans in civil matters including expungements and record sealing.',
    areas: ['Expungement', 'Housing', 'Employment']
  },
  {
    id: 'la3', name: 'ACLU National Prison Project', phone: '202-393-4930', website: 'aclu.org',
    serves: 'National', desc: 'Litigates cases involving unconstitutional prison conditions, excessive punishment, and civil rights.',
    areas: ['Civil Rights', 'Appeals', 'Conditions']
  },
  {
    id: 'la4', name: 'Reentry Legal Assistance', phone: '1-888-237-3933', website: 'reentry.net',
    serves: 'National', desc: 'Specialized reentry legal assistance including ID restoration, benefits, and employment rights.',
    areas: ['Reentry', 'ID Restoration', 'Employment']
  },
];

const FAQ_ITEMS = [
  {
    id: 'faq1', question: 'Can I get my criminal record expunged in Texas?',
    answer: 'In Texas, expungement (legally called "expunction") is available for arrests that did not lead to conviction, deferred adjudication for most Class C misdemeanors, and certain pardoned offenses. If convicted, you may qualify for an "order of nondisclosure" which seals records from most employers. Timeline varies by offense type — 1 to 5 years waiting period after case resolution.',
    category: 'expungement'
  },
  {
    id: 'faq2', question: 'Can an employer legally discriminate because of my record?',
    answer: 'The EEOC guidance states employers cannot have blanket policies excluding all people with criminal records, as this may constitute racial discrimination. Many states have "Ban the Box" laws preventing employers from asking about criminal history on initial applications. Texas has a Ban the Box policy for state government jobs. Private employers vary — but fair chance hiring is growing.',
    category: 'employment'
  },
  {
    id: 'faq3', question: 'What are my rights during a parole/probation search?',
    answer: 'As a condition of parole or probation, you have reduced Fourth Amendment rights. Your supervising officer typically can search your person, vehicle, and residence without a warrant and without probable cause (consent is implied by your conditions). However, police officers without supervision authority still need reasonable suspicion. Know your specific conditions — they vary by case.',
    category: 'probation'
  },
  {
    id: 'faq4', question: 'Can a landlord refuse to rent to me because of my criminal history?',
    answer: 'Landlords can consider criminal history, but they must evaluate applications individually — not apply blanket bans. HUD guidance bars blanket exclusions based on arrest records or outdated convictions. Many cities have local fair chance housing ordinances. If you believe you were unfairly denied, you may file a fair housing complaint.',
    category: 'housing'
  },
  {
    id: 'faq5', question: 'How do I petition for post-conviction relief?',
    answer: 'Post-conviction options include: Direct appeal (must be filed quickly after sentencing), Habeas corpus petition (can raise new evidence or constitutional violations), Motion for new trial, Petition for clemency or pardon. Deadlines are critical. Habeas petitions in federal court generally must be filed within 1 year of conviction becoming final. Consult a post-conviction attorney immediately.',
    category: 'appeals'
  },
];

export default function Legal() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const filteredFaq = FAQ_ITEMS.filter(f => {
    const matchSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCategory === 'all' || f.category === selectedCategory;
    return matchSearch && matchCat;
  });

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="legal-page">
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Legal Resources</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>Know your rights. Find legal aid. Navigate the system.</p>
        </div>

        {/* Disclaimer */}
        <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <AlertCircle size={16} color="#F97316" style={{ flexShrink: 0, marginTop: '1px' }} />
          <p style={{ fontSize: '13px', color: '#AAAAAA', margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: '#F97316' }}>Legal Disclaimer:</strong> Information on this page is for educational purposes only and does not constitute legal advice. For specific legal matters, consult a qualified attorney. Many legal aid organizations offer free consultations.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
          {/* Left: FAQ */}
          <div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Scale size={16} color="#F97316" />
              Common Legal Questions
            </div>

            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '14px' }}>
              <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search legal questions..."
                style={{ width: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '10px 12px 10px 36px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                data-testid="legal-search"
              />
            </div>

            {/* Category Filter */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {[{ id: 'all', label: 'All' }, ...LEGAL_CATEGORIES].map(c => (
                <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                  style={{ padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: `1px solid ${selectedCategory === c.id ? 'var(--orange)' : 'var(--border)'}`, background: selectedCategory === c.id ? 'var(--orange-subtle)' : 'transparent', color: selectedCategory === c.id ? 'var(--orange-text)' : 'var(--text-body)' }}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* FAQ Accordion */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredFaq.map(faq => (
                <div key={faq.id} style={{ background: 'var(--bg-surface)', border: `1px solid ${expandedFaq === faq.id ? 'var(--orange)' : 'var(--border)'}`, borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.2s' }} data-testid={`faq-${faq.id}`}>
                  <button onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '10px' }}>
                    <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', flex: 1 }}>{faq.question}</span>
                    <ChevronRight size={16} color="#555555" style={{ transform: expandedFaq === faq.id ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0, marginTop: '2px' }} />
                  </button>
                  {expandedFaq === faq.id && (
                    <div style={{ padding: '0 16px 16px' }}>
                      <p style={{ fontSize: '13px', color: '#888888', margin: 0, lineHeight: 1.7 }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
              {filteredFaq.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
                  <p>No results found. Try a different search.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Legal Aid Orgs */}
          <div>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Phone size={16} color="#F97316" />
              Legal Aid Organizations
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {LEGAL_AID.map(org => (
                <div key={org.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px' }} data-testid={`legal-org-${org.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>{org.name}</div>
                    <span style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '5px', flexShrink: 0 }}>{org.serves}</span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>{org.desc}</p>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {org.areas.map(a => (
                      <span key={a} style={{ background: 'var(--bg-page)', border: '1px solid var(--border-subtle)', color: '#777777', fontSize: '11px', padding: '2px 8px', borderRadius: '5px' }}>{a}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <a href={`tel:${org.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '8px', padding: '7px 14px', textDecoration: 'none', color: '#F97316', fontSize: '13px', fontWeight: 600 }}>
                      <Phone size={12} /> {org.phone}
                    </a>
                    <a href={`https://${org.website}`} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '7px 14px', textDecoration: 'none', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      <ExternalLink size={12} /> Website
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Know Your Rights Box */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '20px', marginTop: '16px' }}>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '12px' }}>Your Rights at a Glance</div>
              {[
                'You have the right to an attorney if facing criminal charges.',
                'You have the right to remain silent and not incriminate yourself.',
                'You have the right to review your own criminal record.',
                'You cannot be discriminated against solely based on an arrest without conviction.',
                'You have the right to file complaints about civil rights violations.',
              ].map((r) => (
                <div key={r.slice(0, 40)} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F97316', marginTop: '6px', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#888888', lineHeight: 1.5 }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
